import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useAuth } from "@/hooks/use-auth";
import { Redirect } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Trophy,
  Users,
  Calendar,
  Mic,
  ChevronDown,
  ChevronRight,
  Eye,
} from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────────────────

interface CompletedSession {
  id: number;
  quizId: number;
  joinCode: string;
  status: string;
  startedAt: string | null;
  endedAt: string | null;
  quiz: {
    id: number;
    title: string;
    hostName: string | null;
    venue: string | null;
  };
  teamCount: number;
  winner: string | null;
  winnerEmoji: string | null;
  winnerScore: number | null;
}

interface SessionResults {
  session: CompletedSession;
  leaderboard: Array<{
    rank: number;
    teamId: number;
    name: string;
    emoji: string | null;
    score: number;
    roundScores: Record<number, number>;
  }>;
  rounds: Array<{
    id: number;
    title: string;
    roundNumber: number;
    questionCount: number;
  }>;
  answers: Array<{
    teamId: number;
    teamName: string;
    questionId: number;
    questionText: string;
    roundTitle: string;
    answerText: string | null;
    isCorrect: boolean | null;
    pointsAwarded: number;
    markStatus: string;
  }>;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function duration(start: string | null, end: string | null) {
  if (!start || !end) return null;
  const ms = new Date(end).getTime() - new Date(start).getTime();
  const mins = Math.round(ms / 60000);
  return mins < 60 ? `${mins} min` : `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

// ─── Results dialog ───────────────────────────────────────────────────────────

function ResultsDialog({
  sessionId,
  onClose,
}: {
  sessionId: number;
  onClose: () => void;
}) {
  const token = localStorage.getItem("ayrshire_token");
  const [expandedRound, setExpandedRound] = useState<number | null>(null);

  const { data, isLoading } = useQuery<SessionResults>({
    queryKey: ["quiz-results", sessionId],
    queryFn: () =>
      fetch(`/api/admin/quiz/sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => r.json()),
  });

  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {data?.session.quiz.title ?? "Quiz Results"}
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="py-8 text-center text-muted-foreground">Loading results...</div>
        ) : !data ? (
          <div className="py-8 text-center text-muted-foreground">No results found</div>
        ) : (
          <div className="space-y-6">
            {/* Session info */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[
                { label: "Date", value: formatDate(data.session.startedAt) },
                { label: "Duration", value: duration(data.session.startedAt, data.session.endedAt) ?? "—" },
                { label: "Teams", value: data.leaderboard.length.toString() },
              ].map(({ label, value }) => (
                <div key={label} className="bg-muted rounded-lg px-3 py-2">
                  <p className="text-xs text-muted-foreground">{label}</p>
                  <p className="font-semibold">{value}</p>
                </div>
              ))}
            </div>

            {/* Leaderboard */}
            <div>
              <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground mb-3">
                Final Leaderboard
              </h3>
              <div className="space-y-2">
                {data.leaderboard.map(entry => (
                  <div
                    key={entry.teamId}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg ${
                      entry.rank === 1 ? "bg-yellow-50 border border-yellow-200" :
                      entry.rank === 2 ? "bg-slate-50 border border-slate-200" :
                      entry.rank === 3 ? "bg-amber-50 border border-amber-200" :
                      "bg-muted/50"
                    }`}
                  >
                    <span className="text-lg w-8 text-center font-bold">
                      {entry.rank === 1 ? "🥇" : entry.rank === 2 ? "🥈" : entry.rank === 3 ? "🥉" : `#${entry.rank}`}
                    </span>
                    <span className="text-xl">{entry.emoji ?? ""}</span>
                    <span className="flex-1 font-semibold">{entry.name}</span>
                    {/* Per-round scores */}
                    <div className="hidden sm:flex gap-2">
                      {Object.entries(entry.roundScores ?? {}).map(([rid, score]) => {
                        const round = data.rounds.find(r => r.id === Number(rid));
                        return (
                          <div key={rid} className="text-center">
                            <p className="text-xs text-muted-foreground">R{round?.roundNumber ?? rid}</p>
                            <p className="text-sm font-medium">{score}</p>
                          </div>
                        );
                      })}
                    </div>
                    <span className="text-xl font-bold text-primary">{entry.score}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Per-round answers */}
            <div>
              <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground mb-3">
                Answers by Round
              </h3>
              {data.rounds.map(round => {
                const roundAnswers = data.answers.filter(a => a.roundTitle === round.title);
                const isExpanded = expandedRound === round.id;
                return (
                  <div key={round.id} className="border rounded-lg mb-2 overflow-hidden">
                    <button
                      type="button"
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors"
                      onClick={() => setExpandedRound(isExpanded ? null : round.id)}
                    >
                      {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      <span className="font-medium flex-1 text-left">
                        Round {round.roundNumber}: {round.title}
                      </span>
                      <span className="text-sm text-muted-foreground">{round.questionCount} questions</span>
                    </button>
                    {isExpanded && (
                      <div className="border-t">
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-muted/50">
                                <th className="text-left px-4 py-2 font-medium">Team</th>
                                <th className="text-left px-4 py-2 font-medium">Question</th>
                                <th className="text-left px-4 py-2 font-medium">Answer</th>
                                <th className="text-center px-4 py-2 font-medium">Pts</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y">
                              {roundAnswers.map((a, i) => (
                                <tr key={i} className={a.isCorrect ? "bg-green-50" : a.isCorrect === false ? "bg-red-50" : ""}>
                                  <td className="px-4 py-2 font-medium">{a.teamName}</td>
                                  <td className="px-4 py-2 text-muted-foreground max-w-[200px] truncate">{a.questionText}</td>
                                  <td className="px-4 py-2">{a.answerText ?? "—"}</td>
                                  <td className="px-4 py-2 text-center font-semibold">{a.pointsAwarded}</td>
                                </tr>
                              ))}
                              {roundAnswers.length === 0 && (
                                <tr>
                                  <td colSpan={4} className="px-4 py-3 text-center text-muted-foreground">No answers recorded</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function AdminQuizHistory() {
  const { user, isLoading: authLoading } = useAuth();
  const token = localStorage.getItem("ayrshire_token");
  const [viewingId, setViewingId] = useState<number | null>(null);

  const { data: sessions = [], isLoading } = useQuery<CompletedSession[]>({
    queryKey: ["admin-quiz-history"],
    enabled: user?.role === "admin",
    queryFn: () =>
      fetch("/api/admin/quiz/history", {
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => r.json()),
  });

  if (authLoading) return null;
  if (user?.role !== "admin") return <Redirect to="/" />;

  return (
    <AdminLayout current="/admin/quiz/history">
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-primary">Quiz History</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Completed quiz sessions and results</p>
        </div>

        {isLoading ? (
          <div className="text-center py-12 text-muted-foreground">Loading history...</div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Trophy className="h-12 w-12 mx-auto mb-3 text-muted-foreground/50" />
            <p>No completed quizzes yet.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {sessions.map(session => (
              <Card key={session.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex flex-wrap items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h3 className="font-semibold text-base">{session.quiz.title}</h3>
                        <Badge
                          className={
                            session.status === "completed" ? "bg-purple-100 text-purple-700" :
                            session.status === "cancelled" ? "bg-red-100 text-red-700" :
                            "bg-gray-100 text-gray-700"
                          }
                          variant="outline"
                        >
                          {session.status}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                        {session.quiz.hostName && (
                          <span className="flex items-center gap-1">
                            <Mic className="h-3.5 w-3.5" />
                            {session.quiz.hostName}
                          </span>
                        )}
                        {session.quiz.venue && <span>{session.quiz.venue}</span>}
                        {session.startedAt && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3.5 w-3.5" />
                            {formatDate(session.startedAt)}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Users className="h-3.5 w-3.5" />
                          {session.teamCount} teams
                        </span>
                      </div>
                      {session.winner && (
                        <div className="mt-2 flex items-center gap-2">
                          <Trophy className="h-4 w-4 text-yellow-500" />
                          <span className="text-sm font-semibold">
                            {session.winnerEmoji} {session.winner}
                          </span>
                          {session.winnerScore !== null && (
                            <span className="text-sm text-muted-foreground">— {session.winnerScore} pts</span>
                          )}
                        </div>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1"
                      onClick={() => setViewingId(session.id)}
                    >
                      <Eye className="h-3.5 w-3.5" />
                      View Results
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {viewingId && (
        <ResultsDialog sessionId={viewingId} onClose={() => setViewingId(null)} />
      )}
    </AdminLayout>
  );
}
